import { I18nProvider } from '@vlm/i18n';
import { Toaster, UiProvider, usePrefs } from '@vlm/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import type { PlatformRole } from '@vlm/api';
import { messages, useT } from './i18n';
import { ADMINS, restoreSession } from './lib/auth';
import { queryClient } from './lib/query';
import { Layout, RequireRole } from './shell/Layout';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Companies = lazy(() => import('./pages/Companies'));
const CompanyDetail = lazy(() => import('./pages/CompanyDetail'));
const CompanyForm = lazy(() => import('./pages/CompanyForm'));
const CompanyPlan = lazy(() => import('./pages/CompanyPlan'));
const CompanyReason = lazy(() => import('./pages/CompanyReason'));
const CompanyTrial = lazy(() => import('./pages/CompanyTrial'));
const CompanyUserForm = lazy(() => import('./pages/CompanyUserForm'));
const UserPassword = lazy(() => import('./pages/UserPassword'));
const IssueInvoice = lazy(() => import('./pages/IssueInvoice'));
const PayInvoice = lazy(() => import('./pages/PayInvoice'));
const VoidInvoice = lazy(() => import('./pages/VoidInvoice'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Plans = lazy(() => import('./pages/Plans'));
const PlanForm = lazy(() => import('./pages/PlanForm'));
const BillingSettings = lazy(() => import('./pages/BillingSettings'));
const Email = lazy(() => import('./pages/Email'));
const PlatformUsers = lazy(() => import('./pages/PlatformUsers'));
const PlatformUserForm = lazy(() => import('./pages/PlatformUserForm'));
const Profile = lazy(() => import('./pages/Profile'));
const NotFound = lazy(() => import('./pages/NotFound'));

const only = (roles: readonly PlatformRole[], page: ReactNode) => <RequireRole roles={roles}>{page}</RequireRole>;

/** Every form is a page of its own (operator, 2026-10-24: no popup forms) — the …/new, …/edit and step routes below. */
const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Suspense fallback={null}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <Navigate to="/overview" replace /> },
      { path: 'overview', element: <Dashboard /> },
      { path: 'companies', element: <Companies /> },
      { path: 'companies/new', element: <CompanyForm /> },
      { path: 'companies/:companyId', element: <CompanyDetail /> },
      { path: 'companies/:companyId/edit', element: <CompanyForm /> },
      { path: 'companies/:companyId/approve', element: <CompanyPlan action="approve" /> },
      { path: 'companies/:companyId/reject', element: <CompanyReason action="reject" /> },
      { path: 'companies/:companyId/suspend', element: <CompanyReason action="suspend" /> },
      { path: 'companies/:companyId/plan', element: <CompanyPlan action="assign" /> },
      { path: 'companies/:companyId/trial', element: <CompanyTrial /> },
      { path: 'companies/:companyId/invoices/new', element: <IssueInvoice mode="issue" /> },
      { path: 'companies/:companyId/payments/new', element: <IssueInvoice mode="record" /> },
      { path: 'companies/:companyId/invoices/:invoiceId/pay', element: <PayInvoice /> },
      { path: 'companies/:companyId/invoices/:invoiceId/void', element: <VoidInvoice /> },
      { path: 'companies/:companyId/users/new', element: <CompanyUserForm /> },
      { path: 'companies/:companyId/users/:userId/edit', element: <CompanyUserForm /> },
      { path: 'companies/:companyId/users/:userId/password', element: <UserPassword who="company" /> },
      { path: 'invoices', element: <Invoices /> },
      { path: 'invoices/:invoiceId/pay', element: <PayInvoice /> },
      { path: 'invoices/:invoiceId/void', element: <VoidInvoice /> },
      { path: 'plans', element: <Plans /> },
      { path: 'plans/new', element: <PlanForm /> },
      { path: 'plans/:planId/edit', element: <PlanForm /> },
      { path: 'settings/billing', element: <BillingSettings /> },
      { path: 'settings/email', element: <Email /> },
      { path: 'users', element: only(ADMINS, <PlatformUsers />) },
      { path: 'users/new', element: only(ADMINS, <PlatformUserForm />) },
      { path: 'users/:userId/edit', element: only(ADMINS, <PlatformUserForm />) },
      { path: 'users/:userId/password', element: only(ADMINS, <UserPassword who="platform" />) },
      { path: 'profile', element: <Profile /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

function Ui({ children }: { children: ReactNode }) {
  const { t, dir } = useT();
  return (
    <UiProvider dir={dir}>
      {children}
      <Toaster dismissLabel={t('common.dismiss')} className="top-[64px]" />
    </UiProvider>
  );
}

export function App() {
  const lang = usePrefs((p) => p.lang);
  useEffect(() => {
    void restoreSession();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider lang={lang} messages={messages}>
        <Ui>
          <RouterProvider router={router} />
        </Ui>
      </I18nProvider>
    </QueryClientProvider>
  );
}
